import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { fadeUp, staggerParent, viewportOnce } from './motionPresets';

const PERSONAS = [
  {
    id: 'independent',
    title: 'Independent Agents',
    focus: 'Get the support and resources you need to build your book of business with confidence.',
    img: 'https://images.unsplash.com/photo-1696960190591-60d693f4d50d?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
  },
  {
    id: 'producers',
    title: 'Growing Producers',
    focus: 'Access more carrier opportunities, sales training, and tools to increase your production.',
    img: 'https://images.unsplash.com/photo-1780733064275-d1ade9b83a3d?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
  },
  {
    id: 'agencies',
    title: 'Agencies & Teams',
    focus: 'Scalable hierarchy management, downline visibility, and personalized support for growing organizations.',
    img: 'https://images.unsplash.com/photo-1739298061740-5ed03045b280?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
  },
  {
    id: 'expansion',
    title: 'Expand Your Portfolio',
    focus: 'Cross-sell training and certifications to grow into new product lines and markets.',
    img: 'https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
  },
];

export default function AgentPersonas() {
  return (
    <section className="bg-white py-20 sm:py-28 lg:py-32" data-testid="personas-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} className="text-center max-w-2xl mx-auto">
          <motion.span variants={fadeUp} className="text-xs font-semibold uppercase tracking-widest text-[#b8862d]">Built for agents at every stage</motion.span>
          <motion.h2 variants={fadeUp} className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Different goals. One stronger partner.</motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-lg text-slate-600 leading-relaxed">
            Whether you're just getting started or scaling a team, KR provides the support, tools, and opportunities to help you succeed.
          </motion.p>
        </motion.div>

        <motion.div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce}>
          {PERSONAS.map((p) => (
            <motion.div
              key={p.id}
              variants={fadeUp}
              data-testid={`persona-card-${p.id}`}
              className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={p.img} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-slate-900">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.focus}</p>
                <Link to="/become-an-agent" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#b8862d] group-hover:gap-3 transition-all">
                  Learn more <ArrowRight size={15} />
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
